/**
 * TermsOfUsePage - תקנון אתר ותנאי שימוש
 *
 * SOURCE OF TRUTH: "תקנון אתר ותנאי שימוש - יפתח ונגר - יולי 2026.docx" — the
 * version drafted and approved by counsel. The text below is a verbatim
 * transcription including the section/clause numbering (1 / 1.1 / 1.1.1);
 * do NOT reword it. Any change must come from an updated signed document.
 *
 * ONE DELIBERATE DEVIATION: the service address in the opening paragraph is the
 * live one (SITE_URL). The signed .docx names an older domain that no longer
 * serves the app — publishing an address that doesn't resolve would be worse
 * than deviating. Have counsel correct it in the next revision.
 *
 * Public route: /terms — linked from registration, the footer, and the
 * post-signing legal consent modal.
 */

import { Link } from 'react-router-dom'
import LegalDocument, { LegalContact, SITE_URL, type Section } from '../components/legal/LegalDocument'

const LEGAL_PHONE = '055-9859785'
const LEGAL_EMAIL = 'iftach.venger@gmail.com'
const LAST_UPDATED = 'יולי 2026'

const PREAMBLE = [
  <>
    יפתח ונגר מ.ע 034025197 ("החברה" ו/או "מפעיל האתר") מברך את בחירתכם לגלוש באתר האינטרנט ו/או
    במערכת המופעלים על ידו בכתובת: <span dir="ltr">{SITE_URL}</span> ("המערכת"/"האתר").
  </>,
  'האתר מעניק לגולשים מעטפת פיננסית הכוללת חיבור בין ניסיון כלכלי מעמיק לבין פתרונות AI מעשיים, כדי להבטיח תכנון פיננסי עתידי בטוח, ריאלי ומותאם לעסקים ולמשפחות.',
  'כמו כן, המערכת מאפשרת ללקוחות ולמנויים לקבל ייעוץ השקעות באמצעות צ\'אט AI, קביעת פגישות עם יועץ השקעות ומתכנן פנסיוני, פגישות zoom ועוד ("השירותים"). באתר תוכלו למצוא מגוון רב של תכנים ומידע רלוונטי בנוגע לאופן השימוש והרכישה לשירותים המוצעים באתר ("התכנים").',
  'בטרם השימוש במערכת אתה מסכים, מאשר ומצהיר כי ידוע לך וכי אתה מבין שהשימוש במערכת כפוף לתנאי תקנון זה, חתימתך על הסכם ייעוץ השקעות באתר ("הסכם ייעוץ השקעות") והדין הרלוונטי לרבות אך לא רק חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995, חוק ניירות ערך, התשכ"ח-1968 וחוק איסור הלבנת הון, התש"ס-2000.',
  'כמו כן, הינך מסכים מאשר ומצהיר, כי המערכת הינה פלטפורמה דיגיטלית המעניקה שירות מוגבל כמפורט באתר ובהסכם ייעוץ השקעות. המשתמשים במערכת מתחייבים כי יעשו במערכת שימוש רק בהתאם לשימושים המיועדים ולא יבצעו כל שימוש מסחרי ו/או שימוש אסור עפ"י תקנון זה ו/או הסכם ייעוץ השקעות ו/או הדין ואין המערכת אחראית לכל שימוש שיעשה בניגוד לאמור.',
  'המערכת אינה אחראית לשמירה ו/או אבטחה של מידע מאין זה והאחריות הינה על המשתמש בלבד, למעט שמירת מידע המחויבת על פי הדין החל על התקשרות הצדדים.',
  'במידה ואינך מסכים לתנאי התקנון במלואם, אינך רשאי לעשות כל שימוש במערכת ועלייך להפסיק את השימוש בה באופן מיידי.',
]

const SECTIONS: Section[] = [
  {
    title: 'השימוש באתר:',
    clauses: [
      {
        text: 'תקנון ותנאי שימוש אלה מהווים הסכם מחייב בינך, בין אם באופן פרטי ובין באמצעות תאגיד ("אתה") לבין האתר בנוגע לגלישה ושימוש באתר בכל מחשב או מכשיר תקשורת אחר כדוגמת טלפון סלולרי, מחשבי טאבלט וכיו"ב. כמו כן הם חלים על השימוש באתר, בין באמצעות רשת האינטרנט ובין באמצעות כל רשת או אמצעי תקשורת אחרים לרבות גישה לאתר באמצעות הטלפון, אפליקציה או על אמצעי אחר. קרא את התקנון ותנאי השימוש בקפידה, שכן הגלישה באתר וביצוע פעולות בו מעידים על הסכמתך לתנאים הכלולים בתקנון ותנאי שימוש אלו ("התקנון").',
      },
      {
        text: 'במקרה של סתירה בין תקנון זה לבין כל תרגום לשפה אחרת, הגרסה בשפה העברית תגבר בכל עניין משפטי או שאלה של פרשנות או אחרת.',
      },
      {
        text: 'האתר שומר את זכותו לשנות את תנאי התקנון, מעת לעת, על פי שיקול דעתו הבלעדי וללא הודעה מוקדמת. אנו נעדכן בנוגע לשינויים בתקנון על ידי עדכון "העדכון האחרון" בתקנון ואתה מוותר על כל זכות לקבל הודעה על שינויים כאמור.',
      },
      {
        text: 'האמור בתקנון מתייחס באופן שווה לבני שני המינים והשימוש בלשון נקבה ו/או זכר הוא מטעמי נוחות בלבד.',
      },
      {
        text: 'הרכישה והשימוש במערכת מיועד לבעלי כשירות משפטית מגיל 18 בלבד. קטינים מתחת לגיל 18 לא מורשים להשתמש ו/או להירשם למערכת.',
      },
      {
        text: 'שימוש במערכת מחייב חיבור זמין לרשת האינטרנט, ללא חיבור כאמור (מצב Off-line) לא ניתן לעשות שימוש במערכת ו/או לגשת לתכנים שהוזנו על ידי המשתמש.',
      },
      {
        text: 'במקרה שייקבע כי הוראה בתקנון זה אינה ניתנת לאכיפה או שהינה חסרת תוקף מטעם כלשהו, לא יהא בכך כדי להשפיע או לפגוע בחוקיותן, תקפותן ואכיפתן של שאר הוראות התקנון.',
      },
    ],
  },
  {
    title: 'קניין רוחני:',
    clauses: [
      {
        text: 'אלא אם צוין במפורש אחרת, כל זכויות היוצרים והקניין הרוחני באתר ובמערכת, לרבות, עיצובים, תמונות, מאגרי מידע, תוכנות, קוד, שמע, וידאו, טקסט ("התוכן") וכן סמלילים, סימני מסחר וכיו"ב ("הסימנים") הם בבעלות האתר בלבד, או בבעלות צד שלישי, שהרשה לאתר או נתן רישיון לאתר לעשות שימוש על פי דין בתכנים או בסימנים ובכלל זה שותפיו העסקיים של האתר.',
      },
      {
        text: 'התוכן והסימנים ניתנים כמו שהם "AS IS" לשימוש אישי בלבד. אלא אם צוין במפורש אחרת, אין להעתיק, להפיץ, להציג בפומבי, לבצע בפומבי, להעביר לציבור, לשנות, לעבד, ליצור יצירות נגזרות, למכור או להשכיר כל חלק מן הנ"ל, בין ישירות ובין באמצעות או בשיתוף צד שלישי, בכל דרך או אמצעי בין אם אלקטרוניים, מכאניים, אופטיים, אמצעי צילום או הקלטה, או בכל אמצעי ודרך אחרת, בלא קבלת הסכמה בכתב ומראש מהאתר או מבעלי הזכויות האחרים, לפי העניין, ובכפוף לתנאי ההסכמה (ככל שתינתן).',
      },
      {
        text: 'בתנאי שאתה זכאי לגלוש באתר, האתר מקנה לך רישיון מוגבל לשימוש בו ולהורדת או הדפסת עותק של כל חלק מהתוכן אליו ניתנה גישה ראויה לשימוש אישי בלבד, שאינו מסחרי.',
      },
      {
        text: 'כל זכויות היוצרים והקניין הרוחני במוצרים ו/או בתכנים ו/או בשירותים במערכת, למעט תכנים שהועלו על ידי משתמשי המערכת כחלק מהשימוש במערכת, שייכות לאתר ו/או לחברה בלבד, השימוש בתכנים, בשירותים ובמוצרים הוא אישי בלבד ואין להעביר לאחר, להעתיק, להפיץ או להציג בפומבי את המוצרים ו/או השירותים שנרכשו.',
      },
    ],
  },
  {
    title: 'הצהרות משתמש:',
    clauses: [
      {
        text: 'בטרם השימוש באתר, גולשי האתר מצהירים, מאשרים ומסכימים כי ידוע להם:',
        sub: [
          'כל פעולה שתעשה בעקבות שימוש במערכת תעשה במשנה זהירות וכי האחריות הבלעדית לכל נזק (חלילה ויקרה), מכל סוג שהוא, תהיה על עושה השימוש במערכת בלבד.',
          'האתר לא יישא בכל אחריות בגין נזק ו/או הוצאה כספית ו/או הפסד אשר יגרם כתוצאה ממעשה או מחדל אשר בוצע על ידי הלקוח ו/או מי מטעמו לרבות בנוגע לשימוש במערכת ו/או בשירותים.',
          'לקוחות האתר מתחייבים למלא את כל המידע הרלוונטי הנדרש על מנת לקבל את שירותי האתר ולעשות שימוש במערכת, וכי הם יודעים ומסכימים כי המערכת נשענת על המידע המוזן על ידם.',
          'שללקוחות האתר לא תהיה כל טענה כלפי החברה ו/או האתר, מנהליו, עובדיו, שותפיו או מי מטעמו והם לא יהיו אחראים, באופן ישיר או עקיף, לכל נזק לרבות, לגוף, לרכוש או לכל נזק אחר או לכל הפסד אחר, אשר יגרם כתוצאה מקבלת תוכן מהאתר ו/או משימוש במערכת ו/או משירותי האתר, אפילו אם האתר הזהיר, המליץ או ייעץ לעניין מסוים או על שירות מסוים ובשום מקרה האתר לא יהווה תחליף או יישא באחריות הלקוח ו/או מקבל השירות. על הלקוח חלה האחריות וכל סיכון חובה עבור נזק לרכושו ו/או לרכושם של צדדים שלישיים, הנובעים מהשימוש ו/או אי השימוש באתר ו/או בתכנים ו/או בשירותי האתר.',
          'כל פעולה שנעשית על פי המידע במערכת הינה על אחריות עושה הפעולה בלבד.',
          'האתר ו/א החברה ו/או המערכת בשום מקרה לא יהיה אחראי לנזק שנגרם לגולש האתר באמצעות יצירת קשר עם צד שלישי המפרסם את עצמו באתר, או עם שותפיו העסקיים של האתר.',
        ],
      },
      {
        text: 'בעת גלישה באתר ו/או השימוש במערכת, אתה מתחייב ומצהיר כדלקמן: (1) הגלישה באתר והשימוש הם באחריותך הבלעדית; (2) הפרטים שהזנת בעת השארת הפרטים באתר הם נכונים, עדכניים, מדיוקים ומלאים; (3) ככל ויהיה בכך צורך, במקרה של שינוי פרטים תעדכנם בפנייה לאתר; (4) הנך בעל כשירות משפטית ואתה מסכים לתנאי התקנון; (5) לא תעשה שימוש באתר באמצעים אוטומטיים או לא אנושיים, בין אם באמצעות BOT, סקריפט או בכל דרך אחרת; (6) לא תעשה שימוש בלתי חוקי באתר; (7) השימוש שלך באתר לא יפר כל חוק או תקנה רלוונטיים.',
      },
      {
        text: 'האתר ו/או החברה רשאי, למנוע מכל גולש שימוש באתר ו/או במערכת לפי שיקול דעתו המוחלט. מבלי לגרוע מהאמור לעיל, רשאי האתר למנוע גלישת גולש ו/או לחסום גישתו אליו בכל אחד מהמקרים הבאים:',
        sub: [
          'אם בעת השארת פרטים באתר נמסרו במתכוון פרטים שגויים;',
          'במקרה שנעשה שימוש באתר ו/או במערכת לביצוע או כדי לנסות לבצע מעשה בלתי חוקי על-פי דיני מדינת ישראל, או מעשה הנחזה על פניו כבלתי חוקי כאמור, או כדי לאפשר, להקל, לסייע או לעודד ביצועו של מעשה כזה;',
          'במקרה שנעשה שימוש באתר ו/או במערכת בניסיון להתחרות באתר;',
          'אם הופרו תנאי תקנון זה;',
          'אם נעשתה על ידי גולש כל פעולה שתמנע מאחרים לגלוש לאתר או להמשיך ולהשתמש באתר בכל דרך שהיא.',
        ],
      },
      { text: 'אם בעת השארת פרטים באתר נמסרו במתכוון פרטים שגויים, לא עדכניים או לא מלאים.' },
    ],
  },
  {
    title: 'פעילות אסורה באתר ו/או במערכת:',
    clauses: [
      {
        text: 'אינך רשאי להשתמש באתר אלא למטרות שלשמן הוא נועד. השימוש באתר מותר למטרות פרטיות ואישיות בלבד ואין לעשות בו שימוש למטרות מסחריות למעט כאלו שיאושרו על ידי האתר באופן ספציפי.',
      },
      {
        text: 'כמשתמש האתר, אתה מסכים שלא:',
        sub: [
          'לאחזר נתונים או תוכן אחר מהאתר כדי ליצור או להרכיב אוסף, מסד נתונים או מדריך ללא אישור מראש ובכתב מהאתר;',
          'לעשות כל שימוש בעיצובי האתר;',
          'לעשות שימוש לא מורשה באתר, לרבות איסוף כתובות דוא"ל וכיו"ב באמצעים אלקטרוניים או אחרים לצורך שליחת דוא"ל באמצעים אוטומטיים;',
          'לעקוף, להשבית או להפריע בדרך אחרת לאבטחה האתר, לרבות שימוש ביישומים המונעים או מגבילים את השימוש או ההעתקה של תוכן כלשהו;',
          'להונות או להטעות את האתר ו/או את משתמשיו;',
          'לעשות שימוש לא נכון בשירותי התמיכה של האתר או להגיש דוחות כוזבים בנוגע לשימוש באתר;',
          'לעשות שימוש אוטומטי במערכת, כגון שימוש בסקריפטים לשליחת הערות או הודעות, או שימוש בכריית נתונים, רובוטים או כלי איסוף וחילוץ נתונים דומים;',
          'לעשות שימוש העובר על הוראות חוקי הדיוור (ספאם) והפרסום הרלוונטיות במדינה בה משתמש המזמין במערכת ובמדינת היעד בה נמצאים אנשי הקשר;',
          'לנסות להתחזות לאדם אחר;',
          'להשתמש במידע שהתקבל באתר על מנת להטריד, להתעלל או לפגוע באדם אחר;',
          'להשתמש באתר כחלק מכל מאמץ להתחרות באתר;',
          'לאחזר, לפענח או להנדס לאחור חלק מהאתר, אפשרות באתר או יישום באתר;',
          'להטריד, להפחיד או לאיים על כל אחד מעובדי או סוכני האתר;',
          'למחוק את זכויות היוצרים או את הודעת הזכויות הקנייניות מכל תוכן או סימן;',
          'להעתיק או להתאים את קוד האתר או חלק ממנו, כולל אך לא רק Flash, PHP, HTML, JavaScript או קוד אחר;',
          'להעלות או להעביר (או לנסות להעלות או להעביר) וירוסים, סוסים טרויאניים, או חומר אחר, כולל שימוש בדואר זבל, אשר יפריע לשימוש באתר;',
          'לבצע פעולה שתפגע או תזיק לאתר, בהתאם לשיקול דעתו של האתר לרבות שימוש לא הולם בשירותי התמיכה של האתר;',
          'להשתמש באתר באופן שאינו עולה בקנה אחד החוק, התקנות והפסיקה ו/או לעודד/לאפשר שימוש אסור כאמור.',
        ],
      },
      {
        text: 'כל שימוש באתר תוך הפרה של האמור לעיל עלול לגרום, בין היתר, לסיום או השעיית זכויותיך לשימוש באתר.',
      },
    ],
  },
  {
    title: 'תוכן צדדים שלישיים:',
    clauses: [
      {
        text: 'האתר רשאי להשתמש בקישורים לאתרים אחרים ("אתרי צד שלישי") וכן במאמרים, תמונות, טקסט, גרפיקה, תמונות, עיצובים, מוסיקה, סאונד, וידאו, מידע, אפליקציות, תוכנה ותכנים או פריטים אחרים השייכים או שמקורם באתרי צד שלישי לרבות מתן אפשרות לאינטגרציה עם תוכנות וממשקי צד שלישי ("תוכן של צד שלישי"). אתרי צד שלישי ותוכן של צד שלישי אינם נחקרים, מנוטרים, או נבדקים על ידי האתר והאתר לא אחראי לתוכן של צד שלישי שפורסם דרך האתר, שהגעת אליו דרך האתר, שזמין דרך האתר או מותקן בו, כולל תוכן, חוות דעת, אמינות, נוהלי פרטיות או מדיניות אחרת הכלולים באתרי שלישי או בתוכן של צד שלישי. שימוש בקישור או התרת שימוש או התקנה של אתרים של צד שלישי או כל תוכן של צד שלישי כלשהו אינה מרמזת על אישור או אישור לכך על ידינו.',
      },
      {
        text: 'האתר לא לוקח אחריות על כל רכישה או שימוש שתבצע מאתרי צד שלישי או מחברות אחרות אשר יבוצעו ויהיו בתוקף באופן בלעדי בינך לבין הצד השלישי הרלוונטי.',
      },
      {
        text: 'ייתכן והאתר יגבה תשלום מצדדים שלישיים עבור סקירה ו/או המלצה ו/או ביקורת למוצרים ו/או שירותים המסופקים על ידי צדדים שלישיים.',
      },
      {
        text: 'ייתכן והאתר יקבל תגמול מצדדים שלישיים כלשהם עבור פרסום מוצרים ו/או שירותים באתר ו/או עבור שיתופי פעולה עם צדדים שלישיים באתר, בין היתר עבור מכירות שבוצעו באמצעות לינקים לרכישה באתרי צד שלישי.',
      },
    ],
  },
  {
    title: 'רישום משתמש ורכישה:',
    clauses: [
      {
        text: 'רכישת מנוי לקבלת שירותי המערכת תיעשה באמצעות פתיחת פרופיל באתר. בעת הקמת הפרופיל, יתבקש המשתמש להזין את פרטיו המלאים, כגון שם מלא, מספר טלפון, כתובת, ת.ז (נתון לשיקול דעת מנהלי האתר ובהתאם להוראות חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995 וכל דין אחר הרלוונטי לצורך אספקת השירותים). באפשרות הלקוח לעדכן את הפרטים באמצעות המערכת.',
      },
      {
        text: 'כמו כן, על מנת לעשות שימוש במערכת, על המשתמש למלא שאלון סיכון הכולל שאלות בדבר פעולותיו הפיננסיות של המשתמש וזאת לצורך ביצוע הערכת סיכונים ובנית פרופיל סיכון למשתמש לפיו תפעל המערכת. על הלקוח לעדכן את האתר ו/או המערכת מיד עם שינוי בפרטיו ולעדכן את פרופיל הסיכון.',
      },
      {
        text: 'מובהר כי אין חובה על פי חוק למסור את המידע, אולם ללא הזנת הפרטים והנתונים המבוקשים או מסירת פרטים שגויים לא ניתן יהיה להשתמש במערכת ו/או לרכוש באתר.',
      },
      {
        text: 'יובהר, כי שירותי המערכת ניתנים במלואם לשימוש באמצעות התחברות למערכת דרך מחשב בעל חיבור זמין לרשת האינטרנט.',
      },
      {
        text: 'בעת הקמת פרופיל באתר ושימוש במערכת הנך מסכים לשמור את סיסמתך בסודיות, והנך מאשר שאתה תהיה האחראי הבלעדי לכל פעולה או שימוש שיעשו מחשבונך.',
      },
      {
        text: 'אנו שומרים לעצמנו את הזכות להסיר, להשיב, או לשנות כל שם משתמש בו תבחר, במידה ונחליט ששם המשתמש אינו ראוי או מכל סיבה אחרת.',
      },
      {
        text: 'במקרה שהאתר יבקש להחיל שינוי בתנאי ו/או עלות התשלום עבור שירותים שנרכשו במהלך תקופת אספקת השירותים, תישלח הודעה ללקוח במטרה לקבל את אישורו לכך. ככל שהלקוח לא ייתן הסכמתו לשינוי, האתר רשאי לחדול מאספקת השירות בכל עת מבלי שתהא ללקוח כל טענה ו/או תביעה כנגד האתר.',
      },
      {
        text: 'ההרשמה/רכישה לאתר ו/או למערכת תבוצע בכפוף לאישור העסקה על ידי האתר. במקרה בו לא אושרה העסקה על ידי האתר, יקבל הנרשם הודעה וחשבונו לא יחויב בגין העסקה.',
      },
      {
        text: 'מתן השירות יתאפשר בכפוף לשיקול דעתה הבלעדי של האתר והאתר לא יהא אחראי לכל איחור ו/או עיכוב במתן השירות ו/או אי-מתן השירות, כתוצאה מכוח עליון ו/או תקלות טכניות ו/או מאירועים שאינם בשליטתו.',
      },
      {
        text: 'התשלום בגין רכישת מנוי למערכת יתבצע בשקל חדש בלבד (ILS) באמצעות כרטיס אשראי השייך לרוכש או בכל אמצעי תשלום אחר המופיע בעמוד התשלום.',
      },
      {
        text: 'בנוסף להוראות התקנון יחולו גם הוראות תקנון חברת הסליקה או אמצעי התשלום האחר. על אף האמור לעיל, האתר שומר לעצמו את הזכות לקבוע הסדרי תשלומים אחרים לרוכשים, והכל על-פי שיקול דעתו הבלעדי.',
      },
      {
        text: 'ההרשמה והתשלום באתר יבוצעו בכפוף לאישור העסקה על ידי חברת האשראי. במקרה בו לא אושרה העסקה על ידי חברת האשראי, יקבל הרוכש הודעה וחשבונו לא יחויב בגין העסקה.',
      },
    ],
  },
  {
    title: 'התכנים, המחירים, המסלולים והשירותים המוצגים באתר:',
    clauses: [
      { text: 'התכנים והשירותים המוצעים ו/או מוצגים על ידי האתר יופיעו ויוצגו בדפי האתר.' },
      {
        text: 'האתר אינו מחויב, בכל דרך שהיא, לקיים מגוון כלשהו של תכנים ו/או שירותים ו/או מערכות ו/או מנויים ו/או מסלולים.',
      },
      {
        text: 'האתר רשאי בכל עת לשנות את מגוון התכנים, השירותים, מסלולי המנויים והמחירים המוצגים באתר, להחליפם להמעיט מהם, להוסיף עליהם, ללא כל הודעה או התראה מוקדמת.',
      },
      { text: 'אופן הצגת התכנים והשירותים באתר הינו על-פי שיקול דעתו הבלעדי של האתר.' },
    ],
  },
  {
    title: 'הזנת תכנים במערכת:',
    clauses: [
      {
        text: 'בעת שיגור או הזנת תכנים ו/או חומרים ו/או ניהול צ\'אט במערכת, חלה על המשתמש האחריות המלאה והבלעדית לכל תוצאה שתנבע מהזנת התכנים ו/או החומרים ו/או המידע ועליו להקפיד שתכנים כאלה יהיו חוקיים.',
      },
    ],
  },
  {
    title: 'ניהול האתר:',
    clauses: [
      {
        text: 'אנו שומרים לעצמנו את הזכות: (1) לפקח על הפרות של תקנון זה; (2) לנקוט בפעולה משפטית כנגד כל מי שמפר את הוראות החוק או הוראות תקנון זה, לשיקול דעתו הבלעדי של האתר, לרבות, ללא הגבלה, דיווח על המשתמש לרשויות אכיפת החוק; (3) לסרב, להגביל גישה, להגביל זמינות, או להשבית (ככל שניתן מבחינה טכנולוגית) כל תרומה שלך לאתר או כל חלק ממנה, לפי שיקול דעתו הבלעדי של האתר וללא הגבלה; וכן (4) לנהל את האתר באופן שיגן על זכויותיו ורכושו ויקל על תפקודו התקין.',
      },
    ],
  },
  {
    title: 'הרשאה לדיוור, פרסומים ופרסומת:',
    clauses: [
      {
        text: 'גולש שהשאיר פרטים באתר ומצורף לרשימת הדיוור של האתר, מאשר שימוש בפרטיו לצורך קבלת מידע שיווקי, עדכונים ופרסומות שיבצע האתר מעת לעת.',
      },
      { text: 'על גולש שהשאיר פרטים כאמור תחולנה הוראות הדיוור המפורטות בתקנון להלן.' },
      {
        text: 'אין להשאיר פרטים של אדם אחר באתר שלא בהסכמתו ו/או ללא נוכחותו מול המסך בשעת השארת הפרטים ולאחר שהוסברו לו כל תנאי התקנון.',
      },
      {
        text: 'בעת השארת הפרטים יתבקש הגולש למסור פרטים אישיים כגון: שם פרטי, שם משפחה, טלפון וכן כתובת דואר אלקטרוני פעילה. מסירת פרטים חלקיים או שגויים עלולים למנוע את האפשרות להשתמש בשירות ולסכל יצירת קשר במקרה הצורך. במקרה של שינוי פרטים יש לעדכנם באתר.',
      },
      {
        text: 'מובהר כי אין חובה על-פי חוק למסור פרטים באתר, אולם בלא למוסרם לא ניתן יהיה לקבל תוכן שיווקי ועדכונים מהאתר.',
      },
      {
        text: (
          <>
            האתר לא יעשה בפרטים שנמסרו שימוש, אלא בהתאם ל
            <Link to="/privacy" className="text-primary-700 underline hover:text-primary-800">
              מדיניות הפרטיות
            </Link>{' '}
            של האתר המהווה חלק בלתי נפרד מהתקנון.
          </>
        ),
      },
      {
        text: 'השארת פרטים באתר ואישור לקבלת תוכן שיווקי כוללת, בין היתר, קבלת תוכן שיווקי, מידע ביחס למבצעים, עדכונים והנחות המוצעים למשתמשים רשומים.',
      },
      {
        text: 'אישור דיוור (קבלת תוכן שיווקי) כאמור, מהווה את הסכמתו של הגולש למשלוח דברי פרסומת על-פי חוק התקשורת (בזק ושידורים) (תיקון מס\' 40) התשס"ח – 2008 ("חוק התקשורת").',
      },
      {
        text: 'מובהר כי באפשרות משאיר הפרטים להסיר עצמו בכל עת מהדיוור באמצעות לחיצה על "להסרה מרשימת התפוצה" שמופיע בתחתית כל דיוור שנשלח או באמצעות פנייה בדוא"ל לאתר. כל עוד לא הסירו עצמו הנרשם מרשימת הדיוור כאמור, רשאי האתר, בכפוף לחוק התקשורת, להעביר לנרשם דיוור ישיר כאמור.',
      },
      {
        text: 'האתר רשאי למנוע מכל גולש שימוש באתר ו/או לבטל את רישומו לרשימת הדיוור לפי שיקול דעתו המוחלט. מבלי לגרוע מהאמור לעיל, רשאי האתר למנוע גלישת גולש ו/או לבטל רישום לרשימת הדיוור, או לחסום גישה אליו בכל אחד מהמקרים הבאים:',
        sub: [
          'אם בעת השארת הפרטים נמסרו במתכוון פרטים שגויים;',
          'במקרה שנעשה שימוש באתר לביצוע או כדי לנסות לבצע מעשה בלתי חוקי על-פי דיני מדינת ישראל, או מעשה הנחזה על פניו כבלתי חוקי כאמור, או כדי לאפשר, להקל, לסייע או לעודד ביצועו של מעשה כזה;',
          'במקרה שנעשה שימוש באתר בניסיון להתחרות באתר;',
          'אם הופרו תנאי שימוש אלה;',
          'אם נעשתה על ידי גולש כל פעולה שתמנע מאחרים להמשיך ולהשתמש באתר בכל דרך שהיא.',
        ],
      },
      { text: 'אין לראות במידע בדיוור משום הבטחה לתוצאה כלשהי ו/או אחריות לשירות המוצע בו.' },
      {
        text: 'הדיוור בכללותו, לרבות כל המידע המופיע בו, מוצע כמות שהוא, ויהיה מדויק ונכון ככל הניתן, ואולם, יתכן והמידע אינו שלם או לחלופין, יתכן ונפלו טעויות טכניות או אחרות במידע.',
      },
      {
        text: 'הגולש מאשר כי לא תהיה לו כל טענה בקשר למודעות פרסום ו/או פרסומות המוצגות באתר, לרבות בקשר למיקומן באתר. מובהר כי בכל הנוגע למודעות פרסום המוצגות בחסות צד שלישי, לאתר אין כל התערבות בבחירת הפרסומות המוצגות, אמיתות תוכנן וסדר הופעתן.',
      },
    ],
  },
  {
    title: 'הפרת זכויות יוצרים:',
    clauses: [
      {
        text: 'אנו מכבדים את זכויות הקניין של אחרים. אם אתה מאמין כי מידע או תוכן באתר מפר את זכויות קנייניות השייכות לך, אנא צור קשר באמצעות פרטי ההתקשרות שבתחתית תקנון זה.',
      },
    ],
  },
  {
    title: 'סיום:',
    clauses: [
      {
        text: 'תנאי תקנון זה יישארו בתוקפם בעת השימוש באתר. מבלי לגרוע מכל הוראה אחרת בתקנון זה, האתר שומר על זכותו למנוע מכל גולש את השימוש באתר (לרבות חסימת כתובות IP מסוימות), מכל סיבה או נימוק שהוא (מבלי שיצטרך לספק סיבה או נימוק), בשיקול דעתו הבלעדי, ללא צורך בהודעה מוקדמת או התראה ומבלי שיהיה אחראי לנזק כלשהו עקב החלטתו. כמו כן, האתר רשאי להפסיק את השימוש שלך בו וכל תוכן או מידע שפרסמת בכל עת, ללא אזהרה מראש.',
      },
    ],
  },
  {
    title: 'אחריות האתר:',
    clauses: [
      {
        text: 'משתמשי האתר והמערכת מאשרים ומצהירים כי הם מודעים לכך שהאחריות על השימוש במערכת ו/או בשירותים השונים חלה על המשתמש בלבד והיא בכפוף להוראות הסכם ייעוץ ההשקעות שנחתם על ידי המשתמש ובכפוף הוראות חוק הסדרת העיסוק בייעוץ השקעות, בשיווק השקעות ובניהול תיקי השקעות, התשנ"ה-1995, חוק ניירות ערך, התשכ"ח-1968 וחוק איסור הלבנת הון, התש"ס-2000.',
      },
      {
        text: 'השירות מתבסס על נתונים גלויים לציבור. אין התחייבות לרווח. בהשקעות כרוכים סיכונים רבים לרבות הפסד כספי וכי שוק ההון נתון לתנודות.',
      },
      {
        text: 'המידע והשירותים המוצגים במערכת אינם מובאים כתחליף ליעוץ חשבונאי, משפטי, מיסוי או כל ייעוץ אחר מרואה חשבון או עורך דין או כל גורם מקצועי אחר. כל פעולה שנעשית על פי המידע המוצג במערכות הינה על אחריות עושה הפעולה בלבד ובהתאם למידע שהזין המשתמש.',
      },
      {
        text: 'המערכת שומרת לעצמה את הזכות לבצע שינוי \\ להחליף \\ לבטל פיצ\'רים \\ ממשקים שונים לרבות כאלה המתבססים על שירותי צדדים שלישיים בהתאם לשיקול דעתה, בכל עת.',
      },
      {
        text: 'המכשיר בו עושה שימוש המשתמש במסגרת השימוש במערכת הוא באחריותו הבלעדית של המשתמש, לרבות אבטחתו. כמו כן, מובהר, כי פעולות מסוימות שיבצע המשתמש במכשיר, כגון "פריצתו" עשויות לפגוע באבטחת המידע של המערכת והמכשיר וזאת בנוסף להיותן של חלק מפעולות אלה אי חוקיות ואסורות.',
      },
      {
        text: 'מובהר בזאת כי המערכת לא מתחייבת שתפעל 100% מהזמן; כי מעת לעת יבוצעו עדכונים ושדרוגים למערכת – דבר שעלול לגרום להפסקה זמנית במתן השירות; וכי אין למערכת כל שליטה על זמינות השירותים המסופקים על-ידי צדדים שלישיים.',
      },
      {
        text: 'המערכת לא תהיה אחראית כלפי משתמשיה או כלפי צד שלישי כלשהו עבור שינוי, השעיה או הפסקת שירותיה.',
      },
      {
        text: 'המידע המוזן במערכת ו/או באתר הינו באחריות המשתמש בלבד. כל פעולה שתעשה במערכת הינה באחריות המשתמש בלבד והאחריות על הבלעדית לכל נזק (חלילה ויקרה), מכל סוג שהוא, תהיה באחריות המשתמש במערכת בלבד.',
      },
      {
        text: 'במקרה של סיום השימוש במערכת, המערכת ו/או החברה אינה מתחייבת כי תשמור ו/או תגבה את המידע שהוזן במערכת במשך תקופת השימוש בה, ולא תהיה למשתמש כל טענה ו/או תביעה ו/או דרישה כלפי המערכת ו/או החברה בגין מידע ו/או תכנים ו/או חומרים שאינם נשמרו, למעט שמירת המידע על ידי מפעיל האתר כנדרש בדין.',
      },
      {
        text: 'מתן השירות יתאפשר בכפוף לשיקול דעתה הבלעדי של החברה והחברה לא תהא אחראית לכל איחור ו/או עיכוב במתן השירות ו/או אי-מתן השירות, כתוצאה מכוח עליון ו/או תקלות טכניות ו/או מאירועים שאינם בשליטתה.',
      },
      {
        text: 'יובהר כי המערכת ו/או החברה לא תהיה אחראית לכל נזק ו/או הפסד ו/או אובדן נתונים ו/או אובדן רווחים אשר ייגרם כתוצאה מתקלות טכניות במערכת ו/או בעיות תקשורת ו/או בעיכוב מתן השירותים במערכת ו/או בגין אי יכולת להשתמש בשירותי המערכת ו/או שיבוש מידע ו/או שיבושים במערכת ו/או חוסר זמינות של המערכת.',
      },
      {
        text: 'החברה לא תהיה אחראית לכל נזק (ישיר או עקיף), הפסד, עגמת נפש והוצאות שייגרמו למשתמשים ו/או לצדדים שלישיים כלשהם בעקבות שימוש או הסתמכות על כל תוכן, מידע, נתון, מצג, תמונה, וידאו, אודיו, פרסומת, מוצר, שירות וכו\' המופעים במערכת. כל הסתמכות כאמור נעשית על-פי שיקול דעתו ואחריותו הבלעדית של המשתמש במערכת.',
      },
      {
        text: 'השימוש במערכת ייעשה על אחריותו הבלעדית והמלאה של כל משתמש. כל החלטה שתתקבל ביחס לתכנים ו/או לחומרים שיתפרסמו במערכת הינה באחריותו המלאה.',
      },
      {
        text: 'אין לראות במידע המופיע באתר משום הבטחה לתוצאה כלשהי ו/או אחריות לאופן הפעילויות של השירותים המסופקים בו או המידע שמוצג בו. האתר לא יהיה אחראי לשום נזק, ישיר או עקיף, אשר ייגרם לגולש כתוצאה מהסתמכות על מידע המופיע באתר ו/או בקישורים לאתרים אחרים ו/או כל מקור מידע פנימי ו/או חיצוני אחר ו/או שימוש בשירותים ובמידע אשר מוצגים על ידו.',
      },
      {
        text: 'התכנים באתר ניתנים לשימוש כמות שהם ("AS IS"). לא ניתן להתאימם לצרכיו של כל אדם ואדם. לא תהיה לגולש כל טענה, תביעה או דרישה כלפי האתר בגין תכונות של התכנים, יכולותיהם, מגבלותיהם ו/או התאמתם לצרכיו.',
      },
      {
        text: 'האתר בכללותו, לרבות כל המידע המופיע בו, מוצע לציבור כמות שהוא, ויהיה מדויק ונכון ככל הניתן, ואולם, יתכן והמידע אינו שלם או לחלופין, יתכן ונפלו טעויות טכניות או אחרות במידע.',
      },
      {
        text: 'האתר אינו מתחייב כי תכנים ושירותים המתפרסמים באתר, יהיו מלאים, נכונים, חוקיים או מדויקים או יהלמו את ציפיותיו ודרישותיו של כל גולש. האתר לא יישא באחריות כלשהי לכל תוצאה שתנבע מהם, או משימוש בהם, או מהסתמכות עליהם, לרבות: (1) שגיאות, טעויות ואי-דיוקים; (2) נזק לגוף או לרכוש, מכל סוג, הנגרם עקב השימוש באתר ו/או בשירותי האתר; (3) הפרעה בגישה לאתר או מהאתר; (4) כל באג, וירוס, סוסי טרויאני וכיו"ב שעלולים להיות מועברים לאתר על ידי צד שלישי כלשהו.',
      },
      {
        text: 'האתר ממליץ לגולשים באתר לנהוג כצרכנים נבונים וזהירים, ולקרוא בעיון את המידע המוצג באתר ובכלל זה את המידע ביחס לשירות עצמו, תיאורו והתאמתו, כמתואר באתר.',
      },
    ],
  },
  {
    title: 'שינויים באתר, תקלות והפסקות שירות:',
    clauses: [
      {
        text: 'האתר שומר את הזכות לשנות מעת לעת או להסיר את תוכן האתר מכל סיבה שהיא לפי שיקול דעתו הבלעדית וללא הודעה מוקדמת. האתר אינו מחויב לעדכן מידע או תוכן כלשהו באתר.',
      },
      {
        text: 'כל שימוש באתר שלא בהתאם להוראות התקנון ו/או הדין עלול לגרום, בין היתר, לסיום או השעיית זכויותיך לשימוש באתר וחסימת חשבונך.',
      },
      {
        text: 'האתר לא יהיה אחראי כלפיך או כלפי צד שלישי כלשהו עבור שינוי, השעיה או הפסקת שירות כאמור לרבות בגין נזקים שנגרמו לך ו/או לזיכוי או החזרת תשלום.',
      },
      {
        text: 'האתר אינו מתחייב ששירותי האתר לא יופרעו, יינתנו כסדרם או בלא הפסקות, יתקיימו בבטחה וללא טעויות ויהיו חסינים מפני גישה בלתי מורשית למחשבי האתר או מפני נזקים, קלקולים, תקלות או כשלים - והכל, בחומרה, בתוכנה, בקווי ובמערכות תקשורת אצל האתר או אצל מי מספקיו.',
      },
    ],
  },
  {
    title: 'אבטחת מידע:',
    clauses: [
      {
        text: (
          <>
            המערכת ו/או החברה ו/או האתר ומי מטעמם נוקטים צעדים סבירים על מנת לסייע בהגנה על אבטחת
            הנתונים האישיים של המשתמשים והמידע המוזן על ידם. המערכת עושה מאמצים על מנת לאסוף את
            המידע ולאבטחו בהתאם ל
            <Link to="/privacy" className="text-primary-700 underline hover:text-primary-800">
              מדיניות הפרטיות
            </Link>{' '}
            ולחוקים והתקנות החלים במדינת ישראל.
          </>
        ),
      },
      { text: 'המוצר מאוחסן בשרתים תוך שימוש באמצעי אבטחה מתקדמים המקובלים בתעשייה.' },
      {
        text: 'חשוב לזכור שלא ניתן לערוב במאת האחוזים מפני פעילות עוינת ונחושה מצד גורמים זרים ולכן אין בפעולות אלה בטחון מוחלט והמערכת לא מתחייבת שהשירותים בה יהיו חסינים באופן מוחלט מפני גישה בלתי מורשית למידע הנאסף בה, בעקבות פריצה ו/או פגם במערכות המערכת.',
      },
      {
        text: 'למשתמשי המערכת לא תהיה כל טענה ו/או דרישה ו/או תביעה כלפי החברה ו/או האתר ו/או המערכת או מי מטעמם בגין גישה בלתי מורשית למידע שנאסף עליהם על ידי המערכת.',
      },
    ],
  },
  {
    title: 'סמכות שיפוט:',
    clauses: [
      {
        text: 'על תקנון זה והשימוש באתר יחולו אך ורק דיני מדינת ישראל, אולם לא תהיה תחולה לכללי ברירת הדין הבינלאומי הקבועים בהם.',
      },
      {
        text: 'לבתי המשפט במחוז מרכז תהיה סמכות שיפוט ייחודית בכל עניין הנובע ו/או הקשור לתקנון ו/או למחלוקות משפטיות שיתגלו בינך לבין האתר.',
      },
    ],
  },
  {
    title: 'דיוק/ תיקונים טכניים ואחרים:',
    clauses: [
      {
        text: 'האתר בכללותו, לרבות כל המידע המופיע בו, מוצע לציבור כמות שהוא, ויהיה מדויק ונכון ככל הניתן, ואולם, יתכן והמידע אינו שלם או לחלופין, יתכן ונפלו טעויות טכניות או אחרות במידע. האתר שומר על זכותו לתקן טעויות ו/או שגיאות ו/או אי דיוקים כאמור ולעדכן את המידע באתר בכל עת וללא הודעה מוקדמת.',
      },
    ],
  },
  {
    title: 'שונות:',
    clauses: [
      {
        text: 'תנאי תקנון זה מהווים את כל ההסכמות וההבנות בנוגע לשימוש באתר. אי מימוש או אכיפה של זכות או הוראה בתקנון זה, לא תחשב כוויתור מצד האתר על מימוש הזכות או אכיפת ההוראה. האתר יהיה רשאי להמחות לאחרים את כל או חלק מזכויותיו ו/או חובותיו בתקנון זה.',
      },
      {
        text: 'במקרה שייקבע כי הוראה בתקנון זה אינה ניתנת לאכיפה או שהינה חסרת תוקף מטעם כלשהו, לא יהא בכך כדי להשפיע או לפגוע בחוקיותן, תקפותן ואכיפתן של שאר הוראות התקנון.',
      },
    ],
  },
  {
    title: 'צרו קשר:',
    clauses: [
      {
        text: 'האתר מקפיד על קיום הוראות החוק ומכבד את זכותם של משתמשי האתר ואחרים לפרטיות ולשם טוב. אם אתה סבור כי פורסם באתר תוכן הפוגע בך מטעם כלשהו, נא פנה אלינו לפי הפרטים שלהלן ואנו נשתדל לטפל בפנייתך בכל ההקדם. פניות כאמור ניתן להעביר באמצעים הבאים:',
        notes: [<LegalContact phone={LEGAL_PHONE} email={LEGAL_EMAIL} />],
      },
    ],
  },
]

export default function TermsOfUsePage() {
  return (
    <LegalDocument
      title="תקנון אתר ותנאי שימוש"
      subtitle='תקנון ותנאי שימוש לאתר "יפתח ונגר – יעוץ פיננסי חכם"'
      lastUpdated={LAST_UPDATED}
      preamble={PREAMBLE}
      sections={SECTIONS}
    />
  )
}
